import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ProjectsApi, SortBy, SortOrder } from "../api/projectsApi";
import { ProjectShort } from "../model/projectShort";
import { useProjectStore } from "./useProjectStore";

interface ProjectsListActions {
  initialize: () => void;
  loadProjects: () => Promise<void>;
  appNameChanged: (projectId: string, newName: string) => void;
  projectOpened: (projectId: string) => void;
  deleteProject: (projectId: string) => void;
  onSearchQueryChange: (query: string) => void;
  setSortOptions: (sortBy: SortBy, sortOrder: SortOrder) => void;
  clear: () => void;
  dispose: () => void;
}

interface ProjectsListState {
  searchQuery: string;
  projectsMatchingSearchQuery: ProjectShort[] | null;
  allProjects: ProjectShort[] | null;
  sortBy: SortBy;
  sortOrder: SortOrder;

  actions: ProjectsListActions;
}

export const useProjectsListStore = create<ProjectsListState>()(
  persist(
    (set, get) => {
      let unsubscribeLatestProjectEvent: (() => void) | null = null;

      function handleNewProject(projectId: string, appName: string) {
        const existingProject = get().allProjects?.find(
          (project) => project.id === projectId
        );

        if (existingProject) {
          return;
        }

        const newProject: ProjectShort = {
          id: projectId,
          name: appName,
          created_at: new Date().toISOString(),
          last_opened_at: new Date().toISOString(),
        };

        const updatedAllProjects = [newProject, ...(get().allProjects ?? [])];

        set({
          projectsMatchingSearchQuery: updatedAllProjects.filter((project) =>
            project.name?.toLowerCase().includes(get().searchQuery.toLowerCase())
          ),
          allProjects: updatedAllProjects,
        });
      }

      return {
        searchQuery: "",
        projectsMatchingSearchQuery: null,
        allProjects: null,
        sortBy: "last_access" as SortBy,
        sortOrder: "desc" as SortOrder,

        actions: {
          initialize: () => {
            unsubscribeLatestProjectEvent = useProjectStore.subscribe(
              (state) => state.latestMsg,
              (latestMsg) => {
                if (
                  latestMsg?.type ===
                  SharedMessageTypes.MessageTypeEnum.enum.projectConfig
                ) {
                  const projectConfig = latestMsg.content;
                  handleNewProject(
                    useProjectStore.getState().projectId!,
                    projectConfig.projectName
                  );
                }
              }
            );
          },

          loadProjects: async () => {
            const { sortBy, sortOrder, searchQuery } = get();
            const projects = await ProjectsApi.getProjectsForUser({
              sortBy,
              sortOrder,
            });
            set({
              projectsMatchingSearchQuery: projects.filter((project) =>
                project.name?.toLowerCase().includes(searchQuery.toLowerCase())
              ),
              allProjects: projects,
            });
          },

          appNameChanged: (projectId: string, newName: string) => {
            const updatedProjects = [...(get().allProjects ?? [])];
            if (updatedProjects.length === 0) {
              return;
            }
            const projectIndex = updatedProjects.findIndex(
              (project) => project.id === projectId
            );
            if (projectIndex === -1) {
              return;
            }
            updatedProjects[projectIndex].name = newName;

            set({
              projectsMatchingSearchQuery: updatedProjects.filter((project) =>
                project.name
                  ?.toLowerCase()
                  .includes(get().searchQuery.toLowerCase())
              ),
              allProjects: updatedProjects,
            });
          },

          projectOpened: (projectId: string) => {
            const allProjects = get().allProjects;
            if (!allProjects) return;

            const projectIndex = allProjects.findIndex((p) => p.id === projectId);
            if (projectIndex === -1) return;

            const openedProject = {
              ...allProjects[projectIndex],
              last_opened_at: new Date().toISOString(),
            };
            const updatedProjects = [
              openedProject,
              ...allProjects.slice(0, projectIndex),
              ...allProjects.slice(projectIndex + 1),
            ];

            set({
              allProjects: updatedProjects,
              projectsMatchingSearchQuery: updatedProjects.filter((project) =>
                project.name
                  ?.toLowerCase()
                  .includes(get().searchQuery.toLowerCase())
              ),
            });
          },

          deleteProject: (projectId: string) => {
            const allProjects = get().allProjects;
            if (!allProjects) return;

            const updatedProjects = allProjects.filter((p) => p.id !== projectId);
            set({
              allProjects: updatedProjects,
              projectsMatchingSearchQuery: updatedProjects.filter((project) =>
                project.name
                  ?.toLowerCase()
                  .includes(get().searchQuery.toLowerCase())
              ),
            });
          },

          onSearchQueryChange: (query: string) => {
            set({
              searchQuery: query,
              projectsMatchingSearchQuery: (get().allProjects ?? []).filter(
                (project) =>
                  project.name?.toLowerCase().includes(query.toLowerCase())
              ),
            });
          },

          setSortOptions: (sortBy: SortBy, sortOrder: SortOrder) => {
            set({ sortBy, sortOrder });
            get().actions.loadProjects();
          },

          clear: () => {
            set({
              searchQuery: "",
              allProjects: null,
              projectsMatchingSearchQuery: null,
            });
          },

          dispose: () => {
            if (unsubscribeLatestProjectEvent) {
              unsubscribeLatestProjectEvent();
              unsubscribeLatestProjectEvent = null;
            }
            set({
              searchQuery: "",
              allProjects: null,
              projectsMatchingSearchQuery: null,
            });
          },
        },
      };
    },
    {
      name: "darvin-projects-sort",
      partialize: (state) => ({
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      }),
    }
  )
);

export const useProjectsListSearchQuery = () =>
  useProjectsListStore((state) => state.searchQuery);

export const useProjectsMatchingSearchQuery = () =>
  useProjectsListStore((state) => state.projectsMatchingSearchQuery);

export const useProjectsListActions = () =>
  useProjectsListStore((state) => state.actions);

export const useProjectsListSortBy = () =>
  useProjectsListStore((state) => state.sortBy);

export const useProjectsListSortOrder = () =>
  useProjectsListStore((state) => state.sortOrder);
